import { DestroyRef, Injectable, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, firstValueFrom } from 'rxjs';
import { CallCompletionMetrics } from '../models/call-completion-metrics.model';
import { CallStatus } from '../models/call-status.enum';
import { IncomingCallInfo } from '../models/incoming-call-info.model';
import { CallStateStore } from './call-state.store';
import { CallSessionRuntimeService } from './call-session-runtime.service';
import { SipCoreService } from './sip-core.service';
import { DeviceService } from './device.service';
import { AgentDesktopService } from '../../services/agent-desktop.service';
import { SipCredentialStore } from 'src/core/services/agent-status/sip-credential.store';
import { buildOutboundDialDestination } from '../utils/build-outbound-dial-destination';

/**
 * Public facade for all telephony operations.
 * Components must import ONLY this service -- never the store or
 * sip-core directly.
 *
 * Exposes readonly computed signals for reactive state access and
 * delegates all actions to the appropriate internal services.
 */
@Injectable({ providedIn: 'root' })
export class CallFacadeService {
  private readonly store = inject(CallStateStore);
  private readonly sipCore = inject(SipCoreService);
  private readonly deviceService = inject(DeviceService);
  private readonly agentDesktopService = inject(AgentDesktopService);
  private readonly sipCredentialStore = inject(SipCredentialStore);
  private readonly runtime = inject(CallSessionRuntimeService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── Readonly Signals (directly from store -- no re-wrapping) ──

  readonly callStatus = this.store.callStatus;
  readonly currentSession = this.store.currentSession;
  readonly deviceState = this.store.deviceState;
  readonly lastError = this.store.lastError;
  readonly isCallActive = this.store.isCallActive;
  readonly isRegistered = this.store.isRegistered;
  readonly reconnectAttempts = this.store.reconnectAttempts;

  /**
   * An inbound call is ringing and has not been answered yet. Single definition on purpose: the
   * reject flow, AgentDesktopComponent, the status toolbar and CallTabSyncService all gate on it,
   * and the private copies of `status === Ringing && direction === 'inbound'` drifted apart.
   */
  readonly isInboundRinging = computed(() => {
    const status = this.callStatus();
    const session = this.currentSession();
    return status === CallStatus.Ringing && session?.direction === 'inbound';
  });

  /** Whether the current call is muted (false when no session) */
  readonly isMuted = this.store.isMuted;

  /** Whether the current call is on hold (false when no session) */
  readonly isHeld = this.store.isHeld;

  /** Observable for incoming call notifications */
  readonly onIncomingCall$: Observable<IncomingCallInfo> =
    this.sipCore.onIncomingCall$;
  readonly onCallEnded$ = this.sipCore.onCallEnded$;
  readonly sessionId = this.runtime.sessionId;
  readonly callQueueName = this.runtime.queueName;
  readonly sessionPhase = this.runtime.phase;
  readonly isAfterCallWork = this.runtime.isAfterCallWork;
  readonly talkSeconds = this.runtime.talkSeconds;
  readonly holdSeconds = this.runtime.holdSeconds;
  readonly muteSeconds = this.runtime.muteSeconds;
  readonly acwSeconds = this.runtime.acwSeconds;
  readonly talkDuration = this.runtime.talkDuration;
  readonly holdDuration = this.runtime.holdDuration;
  readonly muteDuration = this.runtime.muteDuration;
  readonly acwDuration = this.runtime.acwDuration;
  readonly runtimeMetrics = this.runtime.metrics;

  /** Whether setSinkId is supported in the current browser */
  readonly sinkIdSupported = this.deviceService.sinkIdSupported;

  /** The agent's own SIP extension/phone number */
  readonly agentExtension = this.sipCore.agentExtension;

  /**
   * Tracks an in-flight connect operation so that concurrent callers
   * (AgentDesktopComponent init + AgentStatusService.ensureSipConnected)
   * share a single initialization instead of racing.  Without this guard,
   * a second connect() entering while the first awaits deviceService
   * initialization would trigger a concurrent sipCore.initialize(), which
   * tears down the half-built UserAgent via destroy() -- causing the first
   * connection attempt after a page refresh to fail.
   */
  private connectInFlight: Promise<void> | null = null;

  /**
   * Latched once a rejected registration has already been answered with a credential re-fetch, and
   * cleared only by an explicit {@link connect}. Without it a genuinely unusable credential would
   * loop forever: re-fetch, register, reject, re-fetch.
   */
  private credentialRefreshAttempted = false;

  constructor() {
    this.sipCore.onCredentialsRejected$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(statusCode => {
        void this.onCredentialsRejected(statusCode);
      });
  }

  // ─── Actions ───────────────────────────────────────────────────

  async connect(): Promise<void> {
    // An explicit connect (page load, agent going available) is a fresh start, so it restores the
    // one-shot credential refresh below. The internal retry path calls connectInternal directly
    // and therefore cannot clear its own guard.
    this.credentialRefreshAttempted = false;
    return this.connectInternal();
  }

  private async connectInternal(): Promise<void> {
    if (this.connectInFlight) {
      return this.connectInFlight;
    }

    this.connectInFlight = this.doConnect();
    try {
      await this.connectInFlight;
    } finally {
      this.connectInFlight = null;
    }
  }

  /**
   * Recovers from a registration the SIP stack cannot retry its way out of, which in practice means
   * the per-session SIP secret was revoked while this browser still held it.
   *
   * Re-running the full connect path is what makes this work: it fetches credentials again, and the
   * backend mints them against the agent's current active session, so a session that has been
   * replaced or recreated yields a usable secret. Only one attempt is made — a second rejection is
   * surfaced as an error rather than retried, since it means the failure is not about staleness.
   */
  private async onCredentialsRejected(statusCode: number | null): Promise<void> {
    if (this.credentialRefreshAttempted) {
      this.store.setError(
        statusCode
          ? `SIP registration rejected (${statusCode})`
          : 'SIP registration rejected'
      );
      this.store.transition(CallStatus.Failed);
      return;
    }

    this.credentialRefreshAttempted = true;

    // Re-read before reconnecting. The credentials endpoint is read-only, so this asks the server what the
    // agent's live session holds now — which is a different secret whenever that session was replaced, the
    // day-rollover case being the one that reaches real agents.
    try {
      const credentials = await firstValueFrom(this.agentDesktopService.getSipCredentials());
      this.sipCredentialStore.set(credentials);
    } catch (err) {
      this.reportError('refreshSipCredentials', err);
    }

    await this.connectInternal();
  }

  /**
   * Registers with whatever credentials the agent's session provided.
   *
   * They are read from the store rather than fetched here: the session response carries them, and this
   * method asking for them separately is what used to reach an endpoint that opened a session of its own —
   * two page-load requests each opening one, for a single agent and a single day.
   *
   * An empty store means the session response has not reached here, which every entry point makes sure it
   * has. The fallback below covers the two ways that can still happen — a caller that connected before
   * initializing, and a browser running against an API older than this contract — and it is safe precisely
   * because the endpoint no longer opens a session. If even that yields nothing, the failure is surfaced
   * the way a failed credentials fetch always was, so the agent still sees why registration did not happen.
   */
  private async doConnect(): Promise<void> {
    await this.deviceService.initialize();
    try {
      let credentials = this.sipCredentialStore.value();

      if (!credentials) {
        credentials = await firstValueFrom(this.agentDesktopService.getSipCredentials());
        this.sipCredentialStore.set(credentials);
      }

      await this.sipCore.initialize(credentials);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : String(err);
      this.store.setError(
        `Failed to load SIP configuration: ${message}`
      );
      this.store.transition(CallStatus.Failed);
    }
  }

  /**
   * Places an outbound call, passing `destination` through {@link buildOutboundDialDestination}
   * before handing it to SIP.js. The FreeSWITCH dialplan classifies and translates numbers by shape,
   * so that step only strips cosmetic separators — no prefix, no country code. This is the single
   * point every dial entry point (campaign "Call Now", the manual dialpad number, a dialer search
   * row, queue-override dial) goes through; callers must NOT reshape the destination themselves.
   */
  async makeCall(destination: string, extraHeaders?: string[]): Promise<void> {
    try {
      await this.sipCore.makeCall(
        buildOutboundDialDestination(destination),
        extraHeaders
      );
    } catch (err) {
      this.reportError('makeCall', err);
    }
  }

  async answerCall(): Promise<void> {
    try {
      await this.sipCore.answerCall();
    } catch (err) {
      this.reportError('answerCall', err);
    }
  }

  async hangup(): Promise<void> {
    try {
      await this.sipCore.hangup();
    } catch (err) {
      this.reportError('hangup', err);
    }
  }

  /**
   * Releases a ringing incoming call as an explicit decline (SIP 603) instead of a bare release.
   * Use this wherever the person actually said no - a plain hangup reaches the queue as a no-answer.
   */
  async declineIncoming(): Promise<void> {
    try {
      await this.sipCore.declineIncoming();
    } catch (err) {
      this.reportError('declineIncoming', err);
    }
  }

  async toggleHold(): Promise<void> {
    try {
      await this.sipCore.toggleHold();
    } catch (err) {
      this.reportError('toggleHold', err);
    }
  }

  toggleMute(): void {
    this.sipCore.toggleMute();
  }

  sendDtmf(tones: string): boolean {
    return this.sipCore.sendDtmf(tones);
  }

  selectAudioInput(deviceId: string): void {
    this.deviceService.selectAudioInput(deviceId);
  }

  async selectAudioOutput(deviceId: string): Promise<void> {
    await this.deviceService.selectAudioOutput(deviceId);
  }

  async refreshAudioDevices(): Promise<void> {
    try {
      await this.deviceService.refreshDevices();
    } catch (err) {
      this.reportError('refreshAudioDevices', err);
    }
  }

  async disconnect(): Promise<void> {
    try {
      await this.sipCore.destroy();
    } catch (err) {
      this.reportError('disconnect', err);
    }
  }

  async setAgentAvailability(
    available: boolean,
    options?: { keepRegistration?: boolean; registerWhileUnavailable?: boolean }
  ): Promise<void> {
    try {
      await this.sipCore.setAgentAvailability(available, options);
    } catch (err) {
      this.reportError('setAgentAvailability', err);
    }
  }

  isTransportConnected(): boolean {
    return this.sipCore.isTransportConnected();
  }

  startAfterCallWork(source: 'local' | 'remote' | 'network' | 'manual' = 'manual'): void {
    this.runtime.startAfterCallWork(source);
  }

  completeAfterCallWork(): CallCompletionMetrics | null {
    return this.runtime.completeAfterCallWork();
  }

  setPendingRejectReason(reason: string): void {
    this.runtime.setPendingRejectReason(reason);
  }

  completeRejectedSession(reason: string): void {
    this.runtime.completeRejectedSession(reason);
  }

  private reportError(context: string, err: unknown): void {
    const message = err instanceof Error ? err.message : String(err);
    this.store.setError(`${context}: ${message}`);
  }
}
